import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import DescriptionIcon from "@mui/icons-material/Description";
import type { ChatContact, ChatMessage } from "../types/chatTypes";
import { useState, useEffect } from "react";
import PreviewDialog from "@/components/PreviewDialog/PreviewDialog";
import { privateRequest } from "@/services/axios";
import DownloadIcon from "@mui/icons-material/Download";
import WhatsAppService from "@/services/api/whatsApp";
import { formatUrl } from "@/lib/methodUtils";


interface MessageBubbleProps {
  contact: ChatContact;
  message: ChatMessage;
  search: string;
}

const renderHighlightedText = (text: string, search: string) => {
  if (!search) return text;

  return text.split(new RegExp(`(${search})`, "gi")).map((part, index) =>
    part.toLowerCase() === search.toLowerCase() ? (
      <mark
        key={`${part}-${index}`}
        className="rounded bg-amber-200 px-0.5 text-gray-900 font-medium"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const MessageBubble = ({ contact, message, search }: MessageBubbleProps) => {
  const isMe = message.sender === "me";
  const [previewOpen, setPreviewOpen] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Use message.type as the primary media type indicator
  const isImage = message.type === "image";
  const isVideo = message.type === "video";
  const isFile = message.type === "file" || message.type === "document";
  const hasMedia = isImage || isVideo || isFile;

  useEffect(() => {
    let isMounted = true;

    const loadMedia = async () => {
      // --- TEXT-ONLY: nothing to load ---
      if (!hasMedia) return;

      // --- BLOB URL (optimistic send): already usable, no fetch needed ---
      if (message.fileUrl && message.fileUrl.startsWith("blob:")) {
        if (isMounted) {
          setObjectUrl(message.fileUrl);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(false);

      try {
   
        // --- FILE URL: fetch with auth token ---
        if (message.fileUrl) {
          const token = localStorage.getItem("access_token") || "";
          const res = await fetch(formatUrl(message.fileUrl), {
            headers: { Authorization: token },
          });

          if (!res.ok) throw new Error("Failed to fetch");

          const contentType = res.headers.get("content-type") || "";

          if (contentType.includes("application/json")) {
            const data = await res.json();
            const finalUrl = data.url || data.fileUrl || data.path || data.data?.url;
            if (!finalUrl) throw new Error("No media URL in JSON response");
            if (isMounted) setObjectUrl(formatUrl(finalUrl));
          } else {
            const blob = await res.blob();
            if (isMounted) setObjectUrl(URL.createObjectURL(blob));
          }
          return;
        }
      } catch (err) {
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadMedia();

    return () => {
      isMounted = false;
    };
  }, [message.fileUrl, message.mediaId, hasMedia]);

  const handleDownload = async (url: string, fileName = "file") => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
    }
  };

  return (
    <>
      <div className={`flex items-end gap-2 group ${isMe ? "justify-end" : "justify-start"
        }`}>
        <div
          className={`max-w-[85%] md:max-w-[75%] rounded-2xl ${hasMedia ? "p-1.5" : "px-4 py-2.5"
            } shadow-sm relative transition-all ${isMe
              ? "bg-blue-600 text-white rounded-br-sm shadow-blue-600/10"
              : "bg-white text-gray-800 border border-gray-100 rounded-bl-sm shadow-gray-200/50"
            }`}
        >
          {/* IMAGE */}
          {isImage && (
            <div className="w-[260px] h-[200px] flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden cursor-pointer">
              {loading && <span className="text-2xl animate-pulse">⏳</span>}
              {error && <span className="text-red-500 text-sm">Failed to load</span>}
              {!loading && !error && objectUrl && (
                <img
                  src={objectUrl}
                  className="w-full h-full object-cover"
                  onClick={() => setPreviewOpen(true)}
                  alt="media"
                />
              )}
            </div>
          )}

          {/* VIDEO */}
          {isVideo && (
            <div className="w-[260px] h-[200px] bg-black rounded-xl overflow-hidden flex items-center justify-center">
              {loading && <span className="text-2xl animate-pulse">⏳</span>}
              {error && <span className="text-red-500 text-sm">Failed to load</span>}
              {!loading && !error && objectUrl && (
                <video
                  src={objectUrl}
                  controls
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          )}

          {/* FILE / DOCUMENT */}
          {isFile && (
            <div className={`flex items-center justify-between gap-3 px-3 rounded-xl mb-1 w-[260px] h-[70px] ${isMe ? "bg-blue-700/50" : "bg-gray-50"}`}>
              {error && <span className="text-red-500 text-sm">Failed to load</span>}
              <div className="flex items-center gap-3 min-w-0">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${isMe ? "bg-blue-500" : "bg-gray-200"}`}>
                  <DescriptionIcon sx={{ fontSize: 20, color: isMe ? "white" : "#64748b" }} />
                </div>
                <div className="flex flex-col min-w-0">
                  <p className={`text-sm font-medium truncate ${isMe ? "text-white" : "text-gray-800"}`}>
                    {message.fileName || "Document"}
                  </p>
                  {message.fileSize && (
                    <p className={`text-xs ${isMe ? "text-blue-200" : "text-gray-500"}`}>
                      {message.fileSize}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => objectUrl && handleDownload(objectUrl, message.fileName)}
                disabled={loading || !objectUrl}
                className={`p-2 rounded-full ${isMe ? "hover:bg-blue-500" : "hover:bg-gray-200"}`}
              >
                {loading ? (
                  <span className="text-2xl animate-pulse">⏳</span>
                ) : (
                  <DownloadIcon sx={{ fontSize: 20, color: isMe ? "white" : "#475569" }} />
                )}
              </button>
            </div>
          )}

          {message.text && (
            <p className={`text-[15px] leading-relaxed whitespace-pre-wrap break-words break-all ${hasMedia ? "px-2 pt-2 pb-1" : ""} ${isMe ? "text-white" : "text-gray-800"}`}>
              {renderHighlightedText(message.text, search)}
            </p>
          )}

          <div className={`flex items-center gap-1.5 ${hasMedia ? "px-2 pb-1 pt-0.5 justify-end" : "mt-1 -mb-1 justify-end"} ${isMe ? "text-blue-100" : "text-gray-400"}`}>
            <span className="text-[11px] font-medium tracking-wide">{message.time}</span>
            {isMe &&
              (message.status === "read" ? (
                <DoneAllIcon sx={{ fontSize: 16, color: "#93c5fd" }} />
              ) : message.status === "delivered" || message.status === "sent" ? (
                <DoneAllIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              ) : (
                <DoneIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              ))}
          </div>
        </div>
      </div>

      {previewOpen && objectUrl && (
        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          fileUrl={objectUrl}
          fileName={message.fileName || "Image"}
        />
      )}
    </>
  );
};

export default MessageBubble;
